import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const types = ['student', 'teacher', 'parent'];
const labels = { student: 'Students', teacher: 'Teachers', parent: 'Parents' };

const AdminVideoCallSettings = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/settings`)
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Unable to load settings')))
      .then(data => setRules(data.rules))
      .catch(error => setMessage(error.message));
  }, []);

  const toggleRule = (caller, recipient) => {
    setRules(current => ({
      ...current,
      [caller]: { ...current[caller], [recipient]: !current[caller][recipient] }
    }));
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/calls/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to save settings');
      setRules(data.rules);
      setMessage('Video call permissions saved.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-vibrant-shell min-h-screen p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <header className="app-vibrant-header p-5 md:p-7 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-emerald-700 font-bold">Communication Controls</p>
              <h1 className="text-3xl font-black text-slate-950 mt-1">Video Call Settings</h1>
              <p className="text-slate-600 mt-2">Choose which school user groups may start calls with each other.</p>
            </div>
            <button onClick={() => navigate('/dashboard')} className="bg-slate-800 text-white px-5 py-3 rounded-lg font-bold">
              Back to Dashboard
            </button>
          </div>
        </header>

        <section className="bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-5 bg-emerald-50 border-b border-emerald-100">
            <h2 className="text-xl font-black text-slate-900">Who can call whom</h2>
            <p className="text-sm text-slate-600 mt-1">Rows are callers. Columns are recipients. Changes apply to contact lists and the call API.</p>
          </div>
          {!rules ? (
            <div className="p-10 text-center text-slate-600">Loading call permissions...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[650px]">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="text-left p-4">Caller</th>
                    {types.map(type => <th key={type} className="p-4 text-center">Can call {labels[type]}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {types.map(caller => (
                    <tr key={caller} className="border-b border-slate-200">
                      <th className="text-left p-4 text-slate-900">{labels[caller]}</th>
                      {types.map(recipient => {
                        const parentRestriction = caller === 'parent' && recipient === 'student';
                        const permanentlyBlocked = caller === 'parent' && recipient !== 'student';
                        return (
                          <td key={recipient} className="p-4 text-center">
                            <button
                              type="button"
                              disabled={permanentlyBlocked}
                              onClick={() => !permanentlyBlocked && toggleRule(caller, recipient)}
                              className={`min-w-28 px-4 py-2 rounded-md font-bold ${rules[caller][recipient] && !permanentlyBlocked ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'} ${permanentlyBlocked ? 'cursor-not-allowed opacity-70' : ''}`}
                            >
                              {rules[caller][recipient] && !permanentlyBlocked ? 'Allowed' : 'Blocked'}
                            </button>
                            {parentRestriction && <span className="block text-xs text-slate-500 mt-2">Linked children only</span>}
                            {permanentlyBlocked && <span className="block text-xs text-slate-500 mt-2">Always blocked</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-slate-50">
            <p className={`font-semibold ${message.includes('saved') ? 'text-emerald-700' : 'text-rose-700'}`}>{message}</p>
            <button disabled={!rules || saving} onClick={save} className="bg-emerald-700 disabled:bg-slate-400 text-white px-6 py-3 rounded-lg font-black">
              {saving ? 'Saving...' : 'Save Call Permissions'}
            </button>
          </div>
        </section>

        <aside className="mt-6 border-l-4 border-amber-500 bg-amber-50 p-5 text-slate-800">
          <strong>Parent protection:</strong> parents can only see and call children explicitly linked to their parent account. They cannot call teachers, other parents, or unrelated students unless the system rules change, and unrelated-student calls remain blocked.
        </aside>
      </div>
    </div>
  );
};

export default AdminVideoCallSettings;
